/* eslint-disable react/forbid-prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { MdDelete } from 'react-icons/md';
import { toast } from 'react-toastify';

import { formatDistance } from 'date-fns';
import PropTypes from 'prop-types';

import { DialogContentText } from '@material-ui/core';

import DataTable from '~/components/DataTable';
import LocaleMessage from '~/components/LocaleMessage';
import NewItemButton from '~/components/NewItem/Button';
import SimpleDialog from '~/components/SimpleDialog';
import Splash from '~/components/Splash/Inside';

import history from '~/services/history';
import api from '~/services/pluginbot-api';

export default function ContactLists({ requestError, locale, active }) {
    const [lists, setLists] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [currList, setCurrList] = useState(null);
    const [deleteListOpen, setDeleteListOpen] = useState(false);

    async function loadContactLists() {
        await api
            .get(`contact_lists`)
            .then(async response => {
                const list = response.data.map(l => {
                    const updated = new Date(l.updated);
                    return {
                        ...l,
                        group_name: l.group ? l.group.name : '',
                        source_type: (
                            <LocaleMessage
                                msg={`list.contact.source.${l.source}`}
                            />
                        ),
                        updated: formatDistance(updated, new Date(), {
                            addSuffix: true,
                            locale,
                        }),
                        updated_timestamp: updated.toISOString(),
                    };
                });

                setLists(list);
            })
            .catch(error => requestError(error));
        setIsLoading(false);
    }

    useEffect(() => {
        loadContactLists();
    }, [active]);

    async function deleteList() {
        setIsLoading(true);
        setDeleteListOpen(false);

        await api
            .delete(`contact_lists/${currList}`)
            .then(async () => {
                toast.success(
                    <LocaleMessage msg="page.tools.contact_lists.list.delete_success" />
                );
                loadContactLists();
            })
            .catch(error => requestError(error));

        setIsLoading(false);
    }

    function handleDialogClose(event) {
        event.preventDefault();
        setDeleteListOpen(false);
    }

    function handleDeleteListOpen(event, _id) {
        setCurrList(_id);
        event.preventDefault();
        setDeleteListOpen(true);
    }

    function handleListRowClick(event, id) {
        event.preventDefault();
        setCurrList(id);
        history.push(`/tools/contact_lists/${id}`);
    }

    function renderDeleteListDialog() {
        return (
            <SimpleDialog
                open={deleteListOpen}
                onClose={event => handleDialogClose(event, 'delete')}
                title={
                    <LocaleMessage msg="page.tools.contact_lists.list.delete.confirm" />
                }
                content={
                    <DialogContentText>
                        <LocaleMessage msg="message.recycler.content" />
                    </DialogContentText>
                }
                actions={[
                    {
                        key: 'cancel',
                        onClick: () => setDeleteListOpen(false),
                        label: <LocaleMessage msg="button.cancel" />,
                    },
                    {
                        key: 'remove',
                        onClick: () => deleteList(currList),
                        label: <LocaleMessage msg="button.remove" />,
                    },
                ]}
            />
        );
    }

    function renderLists() {
        const headCells = [
            ...(active.id === '*'
                ? [
                      {
                          id: 'group_name',
                          label: <LocaleMessage msg="table.headers.group" />,
                      },
                  ]
                : []),
            { id: 'name', label: <LocaleMessage msg="table.headers.name" /> },
            {
                id: 'source_type',
                label: <LocaleMessage msg="table.headers.source" />,
            },
            {
                id: 'contact_count',
                label: <LocaleMessage msg="table.headers.contacts" />,
            },
            {
                id: 'updated',
                label: <LocaleMessage msg="table.headers.updated" />,
                order_by: 'updated_timestamp',
            },
        ];

        const rowActions = [
            {
                id: 'delete',
                label: <LocaleMessage msg="button.delete" />,
                icon: <MdDelete />,
                action: handleDeleteListOpen,
            },
        ];

        return (
            <div style={{ minHeight: '150px', width: '100%', padding: '15px' }}>
                <DataTable
                    headerColumns={headCells}
                    data={lists}
                    orderColumn="name"
                    handleTableRowClick={(event, id) =>
                        handleListRowClick(event, id)
                    }
                    rowActions={rowActions}
                    hasHeader
                    header={
                        <>
                            <div className="col-3" style={{ padding: '0px' }}>
                                <NewItemButton
                                    link="/tools/contact_lists/new"
                                    text={
                                        <LocaleMessage msg="page.tools.contact_lists.list.add" />
                                    }
                                    disabled={!active || active.id === '*'}
                                />
                            </div>
                        </>
                    }
                />
            </div>
        );
    }

    return isLoading ? (
        <Splash />
    ) : (
        <>
            {deleteListOpen ? renderDeleteListDialog() : null}
            <div className="row full-body">{renderLists()}</div>
        </>
    );
}

ContactLists.propTypes = {
    requestError: PropTypes.func.isRequired,
    locale: PropTypes.object.isRequired,
    active: PropTypes.object.isRequired,
};
